(() => {
  'use strict';

  const SELECTOR = '[data-bw-proyecta]';
  const PROGRAM_IDS = ['arranca', 'catalogo', 'linaje', 'circulo'];

  class BetterwareProyecta {
    constructor(root) {
      this.root = root;
      this.uid = root.dataset.uid || `pg-${Math.random().toString(36).slice(2)}`;

      this.fmtNumber = new Intl.NumberFormat('es-MX');
      this.fmtMoney = new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        maximumFractionDigits: 0
      });

      this.rules = null;
      this.texts = null;
      this.programMeta = {};
      this.levelColors = { BASE: '#00AEC7', LIDER: '#2A95E5', MASTER: '#0753AD' };
      this.max = { arranca: 0, recluta: 0, linajeChild: 0 };
      this.skipNextAutoSelect = false;

      this.elements = {
        programGrid: root.querySelector('[data-role="program-grid"]'),
        workspace: root.querySelector('[data-role="workspace"]'),
        status: root.querySelector('[data-role="status"]')
      };

      this.state = this.createDefaultState();

      this.bindEvents();
      this.loadConfig();
    }

    createDefaultState() {
      return {
        active: 'arranca',
        arranca: { weeks: Array.from({ length: 6 }, () => ({ order: 0, associates: 0 })) },
        recluta: {
          weeksCount: 4,
          weeks: Array.from({ length: 5 }, () => ({ recruits: 0, referrals: 0 })),
          continuity: 0
        },
        desarrollo: {
          weeksCount: 4,
          target: 'BASE',
          weeks: [0, 0, 0, 0, 0],
          firstLeader: false,
          firstMaster: false,
          hadLeaderBonusBefore: true
        },
        linaje: {
          children: [{ name: 'Hijo 1', firstOrder: 0, associates: 0, purchases: 0 }],
          qualifiedChildren: 0,
          namedNewChild: false
        },
        circulo: { classification: 'LIDER', classifiedChildren: 10, cpChildren: 0, firstTime: true }
      };
    }

    // ---------- Configuración ----------

    loadConfig() {
      this.setStatus('loading', 'Cargando configuración…');

      try {
        const config = this.readEmbeddedJson('config');
        this.applyConfig(config);
        this.texts = this.readEmbeddedJson('texts');
        this.clearStatus();

        const restored = this.restoreState();
        if (!restored) this.buildDevelopmentPlan(true);

        this.render();
      } catch (error) {
        this.setStatus(
          'error',
          error instanceof Error ? error.message : 'No fue posible cargar la configuración.'
        );
      }
    }

    readEmbeddedJson(role) {
      const script = this.root.querySelector(`[data-role="${role}"]`);
      const raw = script?.textContent?.trim();

      if (!raw) {
        throw new Error(
          role === 'config'
            ? 'Selecciona un metaobjeto de configuración para esta sección en el editor de temas.'
            : 'No fue posible cargar los textos de la sección.'
        );
      }

      try {
        return JSON.parse(raw);
      } catch (error) {
        throw new Error('No fue posible interpretar la configuración de la sección.');
      }
    }

    applyConfig(data) {
      if (!data || data.activo === false) {
        throw new Error('Esta sección no está activa actualmente.');
      }

      const programs = Array.isArray(data.programas) ? data.programas : [];
      this.programMeta = {};
      programs.forEach((program) => {
        if (!program || !program.id) return;
        this.programMeta[program.id] = {
          accent: program.accent || '#00AEC7',
          accent1: program.accent1 || program.accent || '#00AEC7',
          accent2: program.accent2 || program.accent || '#00AEC7'
        };
      });

      PROGRAM_IDS.forEach((id) => {
        if (!this.programMeta[id]) {
          this.programMeta[id] = { accent: '#00AEC7', accent1: '#00AEC7', accent2: '#00AEC7' };
        }
      });

      this.levelColors = (data.nivelesDesarrollo && data.nivelesDesarrollo.colores) || this.levelColors;

      const r = data.reglas || {};
      const num = (value) => Number(value) || 0;
      const normalizeTier = (arr) =>
        (Array.isArray(arr) ? arr : []).map((t) => ({ min: num(t.min), pb: num(t.pb) }));
      const normalizeBand = (arr) =>
        (Array.isArray(arr) ? arr : []).map((b) => ({
          min: num(b.min),
          max: b.max === null || b.max === undefined ? Infinity : num(b.max),
          pb: num(b.pb)
        }));

      this.rules = {
        arranca: {
          first: { min: num(r.arranca?.primerPedido?.min), pb: num(r.arranca?.primerPedido?.pb) },
          associates: normalizeTier(r.arranca?.asociados),
          purchases: normalizeTier(r.arranca?.compras)
        },
        recluta: {
          recruits: normalizeBand(r.recluta?.reclutas),
          referrals: normalizeBand(r.recluta?.referidos),
          second: num(r.recluta?.bonoSegundoCatalogo),
          continuityCap: num(r.recluta?.topeContinuidad)
        },
        levels: {
          4: {
            BASE: num(r.niveles?.['4']?.BASE),
            LIDER: num(r.niveles?.['4']?.LIDER),
            MASTER: num(r.niveles?.['4']?.MASTER)
          },
          5: {
            BASE: num(r.niveles?.['5']?.BASE),
            LIDER: num(r.niveles?.['5']?.LIDER),
            MASTER: num(r.niveles?.['5']?.MASTER)
          }
        },
        levelBonus: { LIDER: num(r.bonoNivel?.LIDER), MASTER: num(r.bonoNivel?.MASTER) },
        levelBenefits: {
          BASE: { discount: num(r.beneficiosNivel?.BASE?.descuento), points: num(r.beneficiosNivel?.BASE?.puntos) },
          LIDER: { discount: num(r.beneficiosNivel?.LIDER?.descuento), points: num(r.beneficiosNivel?.LIDER?.puntos) },
          MASTER: { discount: num(r.beneficiosNivel?.MASTER?.descuento), points: num(r.beneficiosNivel?.MASTER?.puntos) }
        },
        linaje: {
          first: { min: num(r.linaje?.primerPedido?.min), pb: num(r.linaje?.primerPedido?.pb) },
          associates: normalizeTier(r.linaje?.asociados),
          purchases: normalizeTier(r.linaje?.compras)
        },
        cp: {
          bonus: {
            BRONCE: num(r.circuloPresidente?.bono?.BRONCE),
            PLATA: num(r.circuloPresidente?.bono?.PLATA),
            ORO: num(r.circuloPresidente?.bono?.ORO),
            PLATINO: num(r.circuloPresidente?.bono?.PLATINO)
          },
          points: {
            BRONCE: num(r.circuloPresidente?.puntos?.BRONCE),
            PLATA: num(r.circuloPresidente?.puntos?.PLATA),
            ORO: num(r.circuloPresidente?.puntos?.ORO),
            PLATINO: num(r.circuloPresidente?.puntos?.PLATINO)
          }
        }
      };

      const last = (arr) => (arr.length ? arr[arr.length - 1] : { pb: 0 });
      this.max = {
        arranca:
          this.rules.arranca.first.pb + last(this.rules.arranca.associates).pb + last(this.rules.arranca.purchases).pb,
        recluta: last(this.rules.recluta.recruits).pb + last(this.rules.recluta.referrals).pb,
        linajeChild:
          this.rules.linaje.first.pb + last(this.rules.linaje.associates).pb + last(this.rules.linaje.purchases).pb
      };
    }

    // ---------- Eventos ----------

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const actionEl = event.target.closest('[data-bwp-action]');
        if (!actionEl || !this.root.contains(actionEl)) return;
        this.handleAction(actionEl);
      });

      this.root.addEventListener('input', (event) => {
        const fieldEl = event.target.closest('[data-bwp-field]');
        if (!fieldEl || !this.root.contains(fieldEl)) return;
        fieldEl.value = fieldEl.value.replace(/[^0-9]/g, '');
        this.handleFieldInput(fieldEl);
      });

      this.root.addEventListener('change', (event) => {
        const selectEl = event.target.closest('[data-bwp-select]');
        if (!selectEl || !this.root.contains(selectEl)) return;
        this.handleSelectChange(selectEl);
      });

      this.root.addEventListener('focusin', (event) => {
        const input = event.target.closest('[data-bwp-field]');
        if (!input) return;

        if (this.skipNextAutoSelect) {
          this.skipNextAutoSelect = false;
          return;
        }

        window.setTimeout(() => input.select(), 10);
      });
    }

    handleAction(el) {
      const action = el.dataset.bwpAction;
      switch (action) {
        case 'select-program':
          this.selectProgram(el.dataset.programId);
          break;
        case 'set-dev-target':
          this.setDevelopmentTarget(el.dataset.level);
          break;
        case 'max-arranca':
          this.maxArranca();
          break;
        case 'max-catalogo':
          this.maxCatalogo();
          break;
        case 'max-all-children':
          this.maxAllChildren();
          break;
        case 'add-child':
          this.addChild();
          break;
        case 'remove-child':
          this.removeChild(Number(el.dataset.index));
          break;
        case 'max-circulo':
          this.maxCirculo();
          break;
        case 'clean-fields':
          this.cleanFields();
          break;
        case 'share-whatsapp':
          this.shareWhatsApp();
          break;
        case 'set-toggle':
          this.setToggle(el.dataset.key, el.dataset.value === 'true');
          break;
        default:
          break;
      }
    }

    handleFieldInput(el) {
      const field = el.dataset.bwpField;
      const index = el.dataset.index !== undefined ? Number(el.dataset.index) : null;
      const value = Math.max(0, Number(el.value || 0));

      switch (field) {
        case 'arranca-order':
          this.state.arranca.weeks[index].order = value;
          break;
        case 'arranca-associates':
          this.state.arranca.weeks[index].associates = value;
          break;
        case 'catalogo-recruits':
          this.state.recluta.weeks[index].recruits = value;
          break;
        case 'catalogo-referrals':
          this.state.recluta.weeks[index].referrals = value;
          break;
        case 'catalogo-continuity':
          this.state.recluta.continuity = Math.min(this.rules.recluta.continuityCap, value);
          break;
        case 'desarrollo-week':
          this.state.desarrollo.weeks[index] = value;
          break;
        case 'linaje-first-order':
          this.state.linaje.children[index].firstOrder = value;
          break;
        case 'linaje-associates':
          this.state.linaje.children[index].associates = value;
          break;
        case 'linaje-purchases':
          this.state.linaje.children[index].purchases = value;
          break;
        case 'linaje-qualified':
          this.state.linaje.qualifiedChildren = value;
          break;
        case 'circulo-classified':
          this.state.circulo.classifiedChildren = value;
          break;
        case 'circulo-cp':
          this.state.circulo.cpChildren = value;
          break;
        default:
          return;
      }

      this.renderWorkspace();
    }

    handleSelectChange(el) {
      const field = el.dataset.bwpSelect;
      switch (field) {
        case 'catalogo-weeks-count':
          this.setCatalogoWeeks(el.value);
          break;
        case 'circulo-classification':
          this.state.circulo.classification = el.value === 'MASTER' ? 'MASTER' : 'LIDER';
          this.renderWorkspace();
          break;
        default:
          break;
      }
    }

    setToggle(key, value) {
      switch (key) {
        case 'firstLeader':
        case 'firstMaster':
        case 'hadLeaderBonusBefore':
          this.state.desarrollo[key] = value;
          break;
        case 'namedNewChild':
          this.state.linaje.namedNewChild = value;
          break;
        case 'firstTime':
          this.state.circulo.firstTime = value;
          break;
        default:
          return;
      }
      this.renderWorkspace();
    }

    selectProgram(id) {
      if (!PROGRAM_IDS.includes(id)) return;
      this.state.active = id;
      this.render();
    }

    // ---------- Utilidades ----------

    tier(value, arr) {
      for (let i = arr.length - 1; i >= 0; i -= 1) {
        if (value >= arr[i].min) return arr[i];
      }
      return { pb: 0, min: 0 };
    }

    band(value, arr) {
      return arr.find((x) => value >= x.min && value <= x.max) || { pb: 0, min: 0, max: 0 };
    }

    nextFor(value, arr) {
      return arr.find((x) => value < x.min) || null;
    }

    pct(value, target) {
      return target ? Math.min(100, (Number(value || 0) / target) * 100) : 100;
    }

    n0(value) {
      return Math.max(0, Number(value) || 0);
    }

    bit(value) {
      return value ? '1' : '0';
    }

    fmt(value) {
      return this.fmtNumber.format(Math.round(Number(value || 0)));
    }

    money(value) {
      return this.fmtMoney.format(Number(value || 0));
    }

    plural(n, singular, pluralForm) {
      return `${this.fmt(n)} ${Number(n) === 1 ? singular : pluralForm}`;
    }

    avg(list) {
      const arr = list.filter((v) => v > 0);
      if (!arr.length) return 0;
      return Math.round(arr.reduce((a, b) => a + b, 0) / arr.length);
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    applyTokens(str, map) {
      return String(str || '').replace(/\{\s*(\w+)\s*\}/g, (_, key) => (key in map ? String(map[key]) : ''));
    }

    // ---------- Estado en la URL ----------

    encodeState() {
      const parts = [this.state.active];

      const a = this.state.arranca.weeks.map((w) => `${this.n0(w.order)}.${this.n0(w.associates)}`).join('-');
      parts.push(`a${a}`);

      const r = this.state.recluta;
      parts.push(
        `r${r.weeksCount}.${this.n0(r.continuity)}.${r.weeks
          .map((w) => `${this.n0(w.recruits)}.${this.n0(w.referrals)}`)
          .join('-')}`
      );

      const d = this.state.desarrollo;
      parts.push(
        `d${d.weeksCount}.${d.target.charAt(0)}.${this.bit(d.firstLeader)}${this.bit(d.firstMaster)}${this.bit(
          d.hadLeaderBonusBefore
        )}.${d.weeks.map((w) => this.n0(w)).join('-')}`
      );

      const l = this.state.linaje;
      parts.push(
        `l${this.n0(l.qualifiedChildren)}.${this.bit(l.namedNewChild)}.${l.children
          .map((ch) => `${this.n0(ch.firstOrder)}.${this.n0(ch.associates)}.${this.n0(ch.purchases)}`)
          .join('-')}`
      );

      const c = this.state.circulo;
      parts.push(`c${c.classification.charAt(0)}.${this.n0(c.classifiedChildren)}.${this.n0(c.cpChildren)}.${this.bit(c.firstTime)}`);

      return parts.join('~');
    }

    decodeState(raw) {
      const parts = raw.split('~');
      if (PROGRAM_IDS.includes(parts[0]) || ['recluta', 'desarrollo'].includes(parts[0])) {
        this.state.active = parts[0];
      }

      parts.slice(1).forEach((chunk) => {
        const key = chunk.charAt(0);
        const body = chunk.slice(1);

        if (key === 'a') {
          const ws = body.split('-').map((w) => w.split('.'));
          this.state.arranca.weeks = Array.from({ length: 6 }, (_, i) => ({
            order: this.n0(ws[i] && ws[i][0]),
            associates: this.n0(ws[i] && ws[i][1])
          }));
        } else if (key === 'r') {
          const f = body.split('.');
          this.state.recluta.weeksCount = Number(f[0]) === 5 ? 5 : 4;
          this.state.recluta.continuity = Math.min(this.rules.recluta.continuityCap, this.n0(f[1]));
          const ws = body.split('.').slice(2).join('.').split('-').map((w) => w.split('.'));
          this.state.recluta.weeks = Array.from({ length: 5 }, (_, i) => ({
            recruits: this.n0(ws[i] && ws[i][0]),
            referrals: this.n0(ws[i] && ws[i][1])
          }));
        } else if (key === 'd') {
          const f = body.split('.');
          this.state.desarrollo.weeksCount = Number(f[0]) === 5 ? 5 : 4;
          this.state.desarrollo.target = { B: 'BASE', L: 'LIDER', M: 'MASTER' }[f[1]] || 'BASE';
          const flags = String(f[2] || '001');
          this.state.desarrollo.firstLeader = flags.charAt(0) === '1';
          this.state.desarrollo.firstMaster = flags.charAt(1) === '1';
          this.state.desarrollo.hadLeaderBonusBefore = flags.charAt(2) !== '0';
          const ws = String(f[3] || '').split('-');
          this.state.desarrollo.weeks = Array.from({ length: 5 }, (_, i) => this.n0(ws[i]));
        } else if (key === 'l') {
          const f = body.split('.');
          this.state.linaje.qualifiedChildren = this.n0(f[0]);
          this.state.linaje.namedNewChild = f[1] === '1';
          const kids = body.split('.').slice(2).join('.').split('-').map((k) => k.split('.'));
          this.state.linaje.children = kids.map((k, i) => ({
            name: `Hijo ${i + 1}`,
            firstOrder: this.n0(k[0]),
            associates: this.n0(k[1]),
            purchases: this.n0(k[2])
          }));
          if (!this.state.linaje.children.length) {
            this.state.linaje.children = [{ name: 'Hijo 1', firstOrder: 0, associates: 0, purchases: 0 }];
          }
        } else if (key === 'c') {
          const f = body.split('.');
          this.state.circulo.classification = f[0] === 'M' ? 'MASTER' : 'LIDER';
          this.state.circulo.classifiedChildren = this.n0(f[1]);
          this.state.circulo.cpChildren = this.n0(f[2]);
          this.state.circulo.firstTime = f[3] === '1';
        }
      });

      if (this.state.active === 'recluta' || this.state.active === 'desarrollo') {
        this.state.active = 'catalogo';
      }
    }

    saveState() {
      try {
        history.replaceState(null, '', `#${this.encodeState()}`);
      } catch (error) {
        // Algunos navegadores en modo privado pueden bloquear history.replaceState.
      }
    }

    restoreState() {
      const raw = decodeURIComponent(location.hash.replace('#', ''));
      if (!raw) return false;
      try {
        this.decodeState(raw);
        return raw.indexOf('~') !== -1;
      } catch (error) {
        return false;
      }
    }

    // ---------- Piezas de render reutilizables ----------

    programTag(id) {
      const text = this.texts.programs[id];
      switch (id) {
        case 'arranca':
          return `Hasta ${this.fmt(this.max.arranca)} PB`;
        case 'linaje':
          return `Hasta ${this.fmt(this.max.linajeChild)} PB por Hijo`;
        default:
          return text.etiqueta || '';
      }
    }

    renderNav() {
      this.elements.programGrid.innerHTML = PROGRAM_IDS.map((id) => {
        const meta = this.programMeta[id];
        const text = this.texts.programs[id];
        const active = this.state.active === id ? ' is-active' : '';
        return `
          <button type="button" class="bwp-programBtn${active}" style="--bwp-accent:${this.escapeHtml(meta.accent)}" data-bwp-action="select-program" data-program-id="${id}">
            <span class="bwp-dot"></span>
            <small>${this.escapeHtml(text.periodo)}</small>
            <strong>${this.escapeHtml(text.nombre)}</strong>
            <em>${this.escapeHtml(this.programTag(id))}</em>
          </button>
        `;
      }).join('');
    }

    shell(id, inner) {
      const meta = this.programMeta[id];
      const text = this.texts.programs[id];
      return `
        <div class="bwp-programSection" style="--bwp-accent:${this.escapeHtml(meta.accent)};--bwp-accent1:${this.escapeHtml(meta.accent1)};--bwp-accent2:${this.escapeHtml(meta.accent2)}">
          <div class="bwp-workspaceHead">
            <div class="bwp-headFlex">
              <div>
                <div class="bwp-eyebrow">Proyección por programa</div>
                <h2>${this.escapeHtml(text.nombre)}</h2>
                <p>${this.escapeHtml(text.copy)}</p>
              </div>
              <div class="bwp-period"><span>Temporalidad</span><b>${this.escapeHtml(text.periodo)}</b></div>
            </div>
          </div>
          <div class="bwp-workspaceBody">
            ${this.renderInstructions(id)}
            ${inner}
            <div class="bwp-footnote">${this.escapeHtml(this.texts.ui.footnote)}</div>
          </div>
        </div>
      `;
    }

    renderInstructions(id) {
      const text = this.texts.programs[id];
      const steps = text.instrucciones || [];
      return `
        <div class="bwp-instructions">
          <div class="bwp-instructionsHead">
            <span>${this.escapeHtml(this.texts.ui.instruccionesLabel)}</span>
            <h3>${this.escapeHtml(this.texts.ui.instruccionesTitulo)}</h3>
          </div>
          <div class="bwp-instructionGrid">
            ${steps
              .map(
                (copy, i) => `
                  <div class="bwp-instructionStep">
                    <div class="bwp-stepNumber">${i + 1}</div>
                    <div class="bwp-instructionStep-copy">${copy}</div>
                  </div>
                `
              )
              .join('')}
          </div>
        </div>
      `;
    }

    progress(title, value, target, pb, copy) {
      return `
        <div class="bwp-progressItem">
          <div class="bwp-progressHead"><strong>${this.escapeHtml(title)}</strong><b>${this.fmt(pb)} PB</b></div>
          <div class="bwp-bar"><i style="width:${this.pct(value, target)}%"></i></div>
          <div class="bwp-progressCopy">${this.escapeHtml(copy)}</div>
        </div>
      `;
    }

    pctBadge(percent) {
      return `<span class="bwp-pctBadge">${Math.round(percent)}%</span>`;
    }

    resultPanel(label, big, unit, caption, rows, totalLabel, total) {
      return `
        <aside class="bwp-resultPanel">
          <div class="bwp-resultLabel">${this.escapeHtml(label)}</div>
          <div class="bwp-resultBig">${big} <span class="bwp-resultUnit">${this.escapeHtml(unit || '')}</span></div>
          <div class="bwp-resultCaption">${this.escapeHtml(caption)}</div>
          <div class="bwp-resultList">
            ${rows
              .map(
                (r) => `
                  <div class="bwp-resultRow">
                    <span>${this.escapeHtml(r[0])}</span>
                    <div class="bwp-resultValue">${r[2] !== undefined ? this.pctBadge(r[2]) : ''}<b>${r[1]}</b></div>
                  </div>
                `
              )
              .join('')}
          </div>
          <div class="bwp-resultTotal"><span>${this.escapeHtml(totalLabel)}</span><b>${total}</b></div>
        </aside>
      `;
    }

    inputField({ label, value, field, index = null, money = false, id = '', bare = false }) {
      const displayVal = value !== undefined && value !== null && value !== 0 ? value : '';
      const idAttr = id ? ` id="${id}"` : '';
      const indexAttr = index !== null ? ` data-index="${index}"` : '';

      const el = `
        <input${idAttr}
          class="bwp-input${money ? ' bwp-money' : ''}"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          value="${this.escapeHtml(displayVal)}"
          aria-label="${this.escapeHtml(label)}"
          data-bwp-field="${field}"${indexAttr}
          placeholder="0"
        >
      `;

      if (bare) return el;

      return `
        <div class="bwp-field">
          <label>${this.escapeHtml(label)}</label>
          ${el}
        </div>
      `;
    }

    // ---------- Arranca y Gana ----------

    calcArranca() {
      const s = this.state.arranca;
      const total = s.weeks.reduce((a, w) => a + Number(w.order || 0), 0);
      const associates = s.weeks.reduce((a, w) => a + Number(w.associates || 0), 0);
      const first = Number(s.weeks[0].order || 0) >= this.rules.arranca.first.min ? this.rules.arranca.first.pb : 0;
      const a = this.tier(associates, this.rules.arranca.associates);
      const p = this.tier(total, this.rules.arranca.purchases);
      return { total, associates, first, a, p, pb: first + a.pb + p.pb };
    }

    renderArranca() {
      const c = this.calcArranca();
      const text = this.texts.programs.arranca;
      const aNext = this.nextFor(c.associates, this.rules.arranca.associates);
      const pNext = this.nextFor(c.total, this.rules.arranca.purchases);
      const maxPurchases = this.rules.arranca.purchases.length
        ? this.rules.arranca.purchases[this.rules.arranca.purchases.length - 1].min
        : 0;
      const maxAssociates = this.rules.arranca.associates.length
        ? this.rules.arranca.associates[this.rules.arranca.associates.length - 1].min
        : 0;
      const maxPurchasesPb = this.rules.arranca.purchases.length
        ? this.rules.arranca.purchases[this.rules.arranca.purchases.length - 1].pb
        : 0;
      const maxAssociatesPb = this.rules.arranca.associates.length
        ? this.rules.arranca.associates[this.rules.arranca.associates.length - 1].pb
        : 0;

      const suggestedWeeklyOrder = Math.ceil(maxPurchases / 6 / 50) * 50;
      const suggestedWeeklyAssoc = Math.ceil(maxAssociates / 6);

      const weeks = this.state.arranca.weeks
        .map(
          (w, i) => `
            <div class="bwp-week">
              <span class="bwp-weekTag">SEMANA ${i + 1}</span>
              ${this.inputField({ label: 'Pedido semanal', value: w.order, field: 'arranca-order', index: i, money: true, id: `${this.uid}-arranca-order-${i}` })}
              ${this.inputField({ label: 'Asociados Nuevos', value: w.associates, field: 'arranca-associates', index: i, id: `${this.uid}-arranca-assoc-${i}` })}
            </div>
          `
        )
        .join('');

      const inner = `
        <div class="bwp-topAction">
          <div class="bwp-copy">
            <strong>${this.escapeHtml(text.topTitulo)}</strong>
            <span>${this.escapeHtml(this.applyTokens(text.topDescripcion, { max: this.fmt(this.max.arranca) }))}</span>
          </div>
          <div class="bwp-topActionButtons">
            <button type="button" class="bwp-primary" data-bwp-action="max-arranca">${this.escapeHtml(text.botonMaximo)} · ${this.fmt(this.max.arranca)} PB</button>
            <button type="button" class="bwp-dangerBtn" data-bwp-action="clean-fields">${this.escapeHtml(this.texts.ui.botonLimpiar)}</button>
            <button type="button" class="bwp-ghost" data-bwp-action="share-whatsapp">${this.escapeHtml(this.texts.ui.botonCompartir)}</button>
          </div>
        </div>

        <div class="bwp-targetCardsGrid">
          <div class="bwp-targetCard">
            <small>Meta Semanal Sugerida de Compras</small>
            <strong>${this.money(suggestedWeeklyOrder)} / sem</strong>
            <span>Para acumular ${this.money(maxPurchases)}+</span>
          </div>
          <div class="bwp-targetCard">
            <small>Meta Semanal Sugerida de Asociados</small>
            <strong>${suggestedWeeklyAssoc} Nuevos / sem</strong>
            <span>Para acumular ${maxAssociates} o más</span>
          </div>
          <div class="bwp-targetCard bwp-targetCard--green">
            <small>Bono Primer Pedido</small>
            <strong>${this.money(this.rules.arranca.first.min)} o más</strong>
            <span>En tu Semana 1</span>
          </div>
        </div>

        <div class="bwp-grid2">
          <div>
            <div class="bwp-panel bwp-soft">
              <h3>Tu estrategia semanal</h3>
              <p class="bwp-sub">Ingresa tus datos de cada semana para calcular tus PB.</p>
              <div class="bwp-weeks">${weeks}</div>
              <div class="bwp-metricGrid">
                <div class="bwp-metric"><small>Compra acumulada</small><b>${this.money(c.total)}</b><span>En 6 semanas</span></div>
                <div class="bwp-metric"><small>Asociados Nuevos</small><b>${this.fmt(c.associates)}</b><span>Con compra válida</span></div>
                <div class="bwp-metric"><small>Avance al máximo</small><b>${Math.round(this.pct(c.pb, this.max.arranca))}%</b><span>de ${this.fmt(this.max.arranca)} PB</span></div>
              </div>
            </div>
            <div class="bwp-panel" style="margin-top:14px">
              <h3>Logros de bienvenida</h3>
              <p class="bwp-sub">Revisa qué bonos ya alcanzaste y cuánto te falta para los siguientes.</p>
              ${this.progress(
                'Primer pedido (Semana 1)',
                this.state.arranca.weeks[0].order,
                this.rules.arranca.first.min,
                c.first,
                c.first
                  ? `Cumplido: primer pedido de ${this.money(this.rules.arranca.first.min)} o más.`
                  : `Te faltan ${this.money(this.rules.arranca.first.min - this.state.arranca.weeks[0].order)} en tu Semana 1 para activar ${this.fmt(this.rules.arranca.first.pb)} PB.`
              )}
              ${this.progress(
                'Asociados Nuevos (Acumulado)',
                c.associates,
                aNext ? aNext.min : maxAssociates,
                c.a.pb,
                aNext
                  ? `Te faltan ${aNext.min - c.associates} asociados para el siguiente bono de ${this.fmt(aNext.pb)} PB.`
                  : `Tope máximo alcanzado de asociados nuevos (${maxAssociates}+).`
              )}
              ${this.progress(
                'Compras acumuladas',
                c.total,
                pNext ? pNext.min : maxPurchases,
                c.p.pb,
                pNext
                  ? `Te faltan ${this.money(pNext.min - c.total)} para el siguiente bono de ${this.fmt(pNext.pb)} PB.`
                  : `Tope máximo alcanzado de compras netas (${this.money(maxPurchases)}+).`
              )}
            </div>
          </div>
          ${this.resultPanel(
            'Tu proyección',
            this.fmt(c.pb),
            'PB',
            'Suma total estimada del programa de bienvenida.',
            [
              ['Bono Primer Pedido', `${this.fmt(c.first)} PB`, this.pct(c.first, this.rules.arranca.first.pb)],
              ['Bono Asociados Nuevos', `${this.fmt(c.a.pb)} PB`, this.pct(c.a.pb, maxAssociatesPb)],
              ['Bono Compras Acumuladas', `${this.fmt(c.p.pb)} PB`, this.pct(c.p.pb, maxPurchasesPb)]
            ],
            'Total acumulado',
            `${this.fmt(c.pb)} PB`
          )}
        </div>
      `;

      return this.shell('arranca', inner);
    }

    maxArranca() {
      this.state.arranca.weeks = [
        { order: 5450, associates: 2 },
        { order: 5450, associates: 2 },
        { order: 5450, associates: 2 },
        { order: 5450, associates: 2 },
        { order: 5450, associates: 2 },
        { order: 5250, associates: 2 }
      ];
      this.renderWorkspace();
    }

    // ---------- Recluta Nuevos Asociados ----------

    calcRecluta() {
      const s = this.state.recluta;
      const ws = s.weeks.slice(0, s.weeksCount);
      const recruits = ws.reduce((a, w) => a + Number(w.recruits || 0), 0);
      const refs = ws.reduce((a, w) => a + Number(w.referrals || 0), 0);
      const rp = this.band(recruits, this.rules.recluta.recruits).pb;
      const fp = this.band(refs, this.rules.recluta.referrals).pb;
      const cont = Math.min(Number(s.continuity || 0), this.rules.recluta.continuityCap) * this.rules.recluta.second;
      return { recruits, refs, rp, fp, cont, subtotal: rp + fp, total: rp + fp + cont };
    }

    // ---------- Plan de Desarrollo ----------

    levelFor(total, weeks) {
      const t = this.rules.levels[weeks];
      if (total >= t.MASTER) return 'MASTER';
      if (total >= t.LIDER) return 'LIDER';
      if (total >= t.BASE) return 'BASE';
      return 'NO_CLASIFICA';
    }

    calcDesarrollo() {
      const s = this.state.desarrollo;
      const total = s.weeks.slice(0, s.weeksCount).reduce((a, v) => a + Number(v || 0), 0);
      const achieved = this.levelFor(total, s.weeksCount);
      const b = achieved === 'NO_CLASIFICA' ? { discount: 0, points: 0 } : this.rules.levelBenefits[achieved];

      let bonus = 0;
      if (achieved === 'LIDER' && s.firstLeader) {
        bonus = this.rules.levelBonus.LIDER;
      } else if (achieved === 'MASTER') {
        if (s.firstMaster) bonus += this.rules.levelBonus.MASTER;
        if (!s.hadLeaderBonusBefore) bonus += this.rules.levelBonus.LIDER;
      }

      return { total, achieved, b, bonus, discountPct: b.discount * 100, pointsPct: b.points * 100 };
    }

    renderCatalogo() {
      const cRec = this.calcRecluta();
      const cDev = this.calcDesarrollo();
      const sDev = this.state.desarrollo;
      const sRec = this.state.recluta;
      const text = this.texts.programs.catalogo;

      const weeksCount = sDev.weeksCount;
      const totalPB = cRec.total + cDev.bonus;
      const targetSales = this.rules.levels[weeksCount][sDev.target];
      const missingSales = Math.max(0, targetSales - cDev.total);
      const balancedWeeklyPurchase = Math.ceil(targetSales / weeksCount / 100) * 100;
      const suggestedWeeklyRecruits = Math.ceil(8 / weeksCount);
      const lastRecruitsPb = this.rules.recluta.recruits.length
        ? this.rules.recluta.recruits[this.rules.recluta.recruits.length - 1].pb
        : 0;
      const lastReferralsPb = this.rules.recluta.referrals.length
        ? this.rules.recluta.referrals[this.rules.recluta.referrals.length - 1].pb
        : 0;

      const cards = ['BASE', 'LIDER', 'MASTER']
        .map((l) => {
          const b = this.rules.levelBenefits[l];
          const t = this.rules.levels[weeksCount][l];
          const cleanDiscount = Math.round(b.discount * 100);
          const cleanPoints = Math.round(b.points * 100);
          const active = sDev.target === l ? ' is-active' : '';
          return `
            <button type="button" class="bwp-programBtn${active}" style="--bwp-accent:${this.escapeHtml(this.levelColors[l] || '#00AEC7')};min-height:112px" data-bwp-action="set-dev-target" data-level="${l}">
              <span class="bwp-dot"></span><small>${l}</small>
              <strong>${cleanDiscount}% descuento · ${cleanPoints}% puntos</strong>
              <em>Meta: ${this.money(t)}</em>
            </button>
          `;
        })
        .join('');

      const weekCards = sDev.weeks
        .slice(0, weeksCount)
        .map(
          (v, i) => `
            <div class="bwp-week">
              <span class="bwp-weekTag">SEMANA ${i + 1}</span>
              ${this.inputField({ label: 'Compra de grupo', value: v, field: 'desarrollo-week', index: i, money: true, id: `${this.uid}-desarrollo-week-${i}` })}
              <div class="bwp-miniGrid" style="margin-top:10px;">
                ${this.inputField({ label: 'Reclutas', value: sRec.weeks[i].recruits, field: 'catalogo-recruits', index: i, id: `${this.uid}-cat-rec-${i}` })}
                ${this.inputField({ label: 'Referidos', value: sRec.weeks[i].referrals, field: 'catalogo-referrals', index: i, id: `${this.uid}-cat-ref-${i}` })}
              </div>
            </div>
          `
        )
        .join('');

      let questionnaireHtml = '';
      if (cDev.achieved === 'LIDER') {
        questionnaireHtml = `
          <div class="bwp-panel" style="margin-top: 14px; border-color: var(--bwp-orange)">
            <h4 style="margin: 0 0 8px; color: var(--bwp-orange); font-size: 14px;">Validación de Bono Único: LÍDER</h4>
            <div class="bwp-switchLine bwp-switchLine--flush">
              <div class="bwp-switchCopy">
                <strong>¿Es tu primera vez alcanzando el nivel Líder?</strong>
                <span>Selecciona "Sí" para proyectar tus ${this.fmt(this.rules.levelBonus.LIDER)} PB de bono único.</span>
              </div>
              <div class="bwp-toggleGroup">
                <button type="button" class="bwp-toggleBtn${sDev.firstLeader ? ' is-active' : ''}" data-bwp-action="set-toggle" data-key="firstLeader" data-value="true">Sí</button>
                <button type="button" class="bwp-toggleBtn${!sDev.firstLeader ? ' is-active' : ''}" data-bwp-action="set-toggle" data-key="firstLeader" data-value="false">No</button>
              </div>
            </div>
          </div>
        `;
      } else if (cDev.achieved === 'MASTER') {
        questionnaireHtml = `
          <div class="bwp-panel" style="margin-top: 14px; border-color: var(--bwp-purple)">
            <h4 style="margin: 0 0 8px; color: var(--bwp-purple); font-size: 14px;">Validación de Bonos Únicos: MÁSTER</h4>
            <div class="bwp-switchLine" style="padding: 10px 0; border: 0;">
              <div class="bwp-switchCopy">
                <strong>¿Es tu primera vez alcanzando el nivel Máster?</strong>
                <span>Selecciona "Sí" para proyectar tus ${this.fmt(this.rules.levelBonus.MASTER)} PB de bono único.</span>
              </div>
              <div class="bwp-toggleGroup">
                <button type="button" class="bwp-toggleBtn${sDev.firstMaster ? ' is-active' : ''}" data-bwp-action="set-toggle" data-key="firstMaster" data-value="true">Sí</button>
                <button type="button" class="bwp-toggleBtn${!sDev.firstMaster ? ' is-active' : ''}" data-bwp-action="set-toggle" data-key="firstMaster" data-value="false">No</button>
              </div>
            </div>
            <div class="bwp-switchLine" style="padding: 10px 0;">
              <div class="bwp-switchCopy">
                <strong>Durante tu trayectoria, ¿ya habías cobrado el bono de Líder?</strong>
                <span>Si marcas "No", el sistema acumulará ambos bonos automáticamente (+${this.fmt(this.rules.levelBonus.LIDER)} PB extra).</span>
              </div>
              <div class="bwp-toggleGroup">
                <button type="button" class="bwp-toggleBtn${sDev.hadLeaderBonusBefore ? ' is-active' : ''}" data-bwp-action="set-toggle" data-key="hadLeaderBonusBefore" data-value="true">Sí</button>
                <button type="button" class="bwp-toggleBtn${!sDev.hadLeaderBonusBefore ? ' is-active' : ''}" data-bwp-action="set-toggle" data-key="hadLeaderBonusBefore" data-value="false">No</button>
              </div>
            </div>
          </div>
        `;
      }

      const inner = `
        <div class="bwp-topAction">
          <div class="bwp-copy">
            <strong>${this.escapeHtml(text.topTitulo)}</strong>
            <span>${this.escapeHtml(text.topDescripcion)}</span>
          </div>
          <div class="bwp-topActionButtons">
            <button type="button" class="bwp-primary" data-bwp-action="max-catalogo">${this.escapeHtml(text.botonMaximo)}</button>
            <button type="button" class="bwp-dangerBtn" data-bwp-action="clean-fields">${this.escapeHtml(this.texts.ui.botonLimpiar)}</button>
            <button type="button" class="bwp-ghost" data-bwp-action="share-whatsapp">${this.escapeHtml(this.texts.ui.botonCompartir)}</button>
          </div>
        </div>

        <div class="bwp-panel bwp-soft" style="margin-bottom:14px;">
          <h3>Estructura del catálogo</h3>
          <p class="bwp-sub">Elige la duración de tu catálogo actual.</p>
          <div class="bwp-inlineFields" style="grid-template-columns: 1fr;">
            <div class="bwp-field">
              <label>Duración del catálogo</label>
              <select class="bwp-select" data-bwp-select="catalogo-weeks-count">
                <option value="4"${weeksCount === 4 ? ' selected' : ''}>4 semanas (Catálogo regular)</option>
                <option value="5"${weeksCount === 5 ? ' selected' : ''}>5 semanas (Catálogo largo)</option>
              </select>
            </div>
          </div>
        </div>

        <div class="bwp-targetCardsGrid">
          <div class="bwp-targetCard">
            <small>Meta Semanal de Compra</small>
            <strong>${this.money(balancedWeeklyPurchase)} / sem</strong>
            <span>Para nivel ${sDev.target}</span>
          </div>
          <div class="bwp-targetCard">
            <small>Meta Semanal de Ingresos</small>
            <strong>${suggestedWeeklyRecruits} directos / sem</strong>
            <span>Para tope de 8+</span>
          </div>
        </div>

        <div class="bwp-grid2">
          <div>
            <div class="bwp-panel bwp-soft">
              <h3>Nivel Comercial Objetivo</h3>
              <p class="bwp-sub">Elige el nivel que quieres alcanzar. Distribuiremos la meta de compra entre las semanas y podrás ajustarla.</p>
              <div class="bwp-programGrid" style="grid-template-columns:repeat(3,1fr);margin-top:14px">${cards}</div>
            </div>
            <div class="bwp-panel" style="margin-top:14px">
              <h3>Estrategia Semanal Integral</h3>
              <p class="bwp-sub">Ingresa tus compras de grupo, Reclutas y Referidos de cada semana.</p>
              <div class="bwp-weeks">${weekCards}</div>
              <div class="bwp-metricGrid">
                <div class="bwp-metric"><small>Compra del grupo</small><b>${this.money(cDev.total)}</b><span>Meta ${this.money(targetSales)}</span></div>
                <div class="bwp-metric"><small>Avance de nivel</small><b>${Math.round(this.pct(cDev.total, targetSales))}%</b><span>${missingSales ? `Faltan ${this.money(missingSales)}` : 'Meta lograda'}</span></div>
                <div class="bwp-metric"><small>Ingresos totales</small><b>${this.fmt(cRec.recruits + cRec.refs)}</b><span>${this.fmt(cRec.recruits)} directos / ${this.fmt(cRec.refs)} referidos</span></div>
              </div>

              <div style="margin-top:20px; padding-top:15px; border-top: 1px dashed var(--bwp-line)">
                <h3>Asociados que continúan activos</h3>
                <p class="bwp-sub">Gana ${this.fmt(this.rules.recluta.second)} PB por cada Asociado que haga compras en su segundo catálogo (máximo ${this.rules.recluta.continuityCap}).</p>
                ${this.inputField({ label: 'Asociados en su 2.º catálogo', value: sRec.continuity, field: 'catalogo-continuity', id: `${this.uid}-cat-continuity` })}
              </div>

              <div style="margin-top:20px; padding-top:15px; border-top: 1px dashed var(--bwp-line)">
                <h3>¿Es tu primera vez en este nivel?</h3>
                <div class="bwp-sub" style="margin-bottom: 0;">Si es la primera vez que alcanzas Líder o Máster, puedes ganar un Bono Único adicional.</div>
                ${questionnaireHtml}
              </div>
            </div>
          </div>
          ${this.resultPanel(
            'Beneficios y Puntos',
            cDev.achieved === 'NO_CLASIFICA' ? '—' : cDev.achieved.replace('_', ' '),
            '',
            'Con esta proyección, operarías con el nivel proyectado y sus beneficios directos en tu próximo catálogo.',
            [
              ['Compra grupal total', this.money(cDev.total)],
              ['Descuento desbloqueado', cDev.achieved === 'NO_CLASIFICA' ? '—' : `${Math.round(cDev.discountPct)}%`],
              ['% Puntos desbloqueado', cDev.achieved === 'NO_CLASIFICA' ? '—' : `${Math.round(cDev.pointsPct)}%`],
              ['PB por Reclutamiento', `${this.fmt(cRec.total)} PB`, this.pct(cRec.subtotal, lastRecruitsPb + lastReferralsPb)],
              ['Bono único por logro', `${this.fmt(cDev.bonus)} PB`]
            ],
            'Puntos del catálogo',
            `${this.fmt(totalPB)} PB`
          )}
        </div>
      `;

      return this.shell('catalogo', inner);
    }

    setDevelopmentTarget(level) {
      if (!['BASE', 'LIDER', 'MASTER'].includes(level)) return;
      this.state.desarrollo.target = level;
      this.buildDevelopmentPlan();
    }

    buildDevelopmentPlan(silent = false) {
      const s = this.state.desarrollo;
      const target = this.rules.levels[s.weeksCount][s.target];
      const each = Math.ceil(target / s.weeksCount / 100) * 100;
      s.weeks = [0, 0, 0, 0, 0];
      for (let i = 0; i < s.weeksCount; i += 1) {
        s.weeks[i] = each;
      }
      if (!silent) this.renderWorkspace();
    }

    setCatalogoWeeks(value) {
      const weeksCount = Number(value) === 5 ? 5 : 4;
      this.state.desarrollo.weeksCount = weeksCount;
      this.state.recluta.weeksCount = weeksCount;
      this.buildDevelopmentPlan();
    }

    maxCatalogo() {
      this.state.desarrollo.target = 'MASTER';
      this.state.desarrollo.firstMaster = true;
      this.state.desarrollo.hadLeaderBonusBefore = false;
      this.buildDevelopmentPlan(true);

      const weeksCount = this.state.desarrollo.weeksCount;
      this.state.recluta.weeks = Array.from({ length: 5 }, (_, i) => ({
        recruits: i < weeksCount ? 2 : 0,
        referrals: i < weeksCount ? 2 : 0
      }));
      this.state.recluta.continuity = 16;
      this.renderWorkspace();
    }

    // ---------- Haz Linaje y Gana Más ----------

    calcChild(ch) {
      const first = ch.firstOrder >= this.rules.linaje.first.min ? this.rules.linaje.first.pb : 0;
      const a = this.tier(ch.associates, this.rules.linaje.associates);
      const p = this.tier(ch.purchases, this.rules.linaje.purchases);
      return { first, a, p, total: first + a.pb + p.pb };
    }

    calcLinaje() {
      const s = this.state.linaje;
      const children = s.children.map((ch) => ({ ...ch, calc: this.calcChild(ch) }));
      const childBonuses = children.reduce((a, ch) => a + ch.calc.total, 0);

      let d = 0;
      let pr = 0;
      if (s.namedNewChild) {
        d = 0.06;
        pr = 1;
      } else if (s.qualifiedChildren >= 5) {
        d = 0.04;
        pr = 0.5;
      } else if (s.qualifiedChildren >= 1) {
        d = 0.02;
        pr = 0.5;
      }

      let level = 'Sin beneficio desbloqueado';
      if (s.namedNewChild) level = 'Nombramiento de Hijo Nuevo';
      else if (s.qualifiedChildren >= 10) level = '10 o más Hijos · revisa Círculo del Presidente';
      else if (s.qualifiedChildren >= 5) level = 'De 5 a 9 Hijos clasificados';
      else if (s.qualifiedChildren >= 1) level = 'De 1 a 4 Hijos clasificados';

      return { children, childBonuses, d, pr, level };
    }

    renderLinaje() {
      const c = this.calcLinaje();
      const s = this.state.linaje;
      const text = this.texts.programs.linaje;
      const lastPurchase = this.rules.linaje.purchases.length
        ? this.rules.linaje.purchases[this.rules.linaje.purchases.length - 1].min
        : 0;
      const suggestedWeeklyChildOrder = Math.ceil(lastPurchase / 6 / 100) * 100;
      const lastLinajeAssociatesPb = this.rules.linaje.associates.length
        ? this.rules.linaje.associates[this.rules.linaje.associates.length - 1].pb
        : 0;
      const lastLinajePurchasesPb = this.rules.linaje.purchases.length
        ? this.rules.linaje.purchases[this.rules.linaje.purchases.length - 1].pb
        : 0;

      const childHtml = c.children
        .map(
          (ch, i) => `
            <div class="bwp-child">
              <div class="bwp-childHead">
                <strong>${this.escapeHtml(ch.name)}</strong>
                <span>${this.fmt(ch.calc.total)} PB</span>
                ${c.children.length > 1 ? `<button type="button" class="bwp-remove" data-bwp-action="remove-child" data-index="${i}">Quitar</button>` : ''}
              </div>
              <div class="bwp-inlineFields">
                ${this.inputField({ label: 'Primer pedido', value: ch.firstOrder, field: 'linaje-first-order', index: i, money: true, id: `${this.uid}-linaje-first-${i}` })}
                ${this.inputField({ label: 'Asociados Nuevos (6 semanas)', value: ch.associates, field: 'linaje-associates', index: i, id: `${this.uid}-linaje-assoc-${i}` })}
                ${this.inputField({ label: 'Compras netas acumuladas', value: ch.purchases, field: 'linaje-purchases', index: i, money: true, id: `${this.uid}-linaje-purchases-${i}` })}
              </div>
              <div class="bwp-resultList">
                <div class="bwp-resultRow"><span>Bono Primer pedido</span><div class="bwp-resultValue">${this.pctBadge(this.pct(ch.calc.first, this.rules.linaje.first.pb))}<b>${this.fmt(ch.calc.first)} PB</b></div></div>
                <div class="bwp-resultRow"><span>Bono Asociados Nuevos</span><div class="bwp-resultValue">${this.pctBadge(this.pct(ch.calc.a.pb, lastLinajeAssociatesPb))}<b>${this.fmt(ch.calc.a.pb)} PB</b></div></div>
                <div class="bwp-resultRow"><span>Bono Compras netas</span><div class="bwp-resultValue">${this.pctBadge(this.pct(ch.calc.p.pb, lastLinajePurchasesPb))}<b>${this.fmt(ch.calc.p.pb)} PB</b></div></div>
              </div>
            </div>
          `
        )
        .join('');

      const inner = `
        <div class="bwp-topAction">
          <div class="bwp-copy">
            <strong>${this.escapeHtml(text.topTitulo)}</strong>
            <span>${this.escapeHtml(text.topDescripcion)}</span>
          </div>
          <div class="bwp-topActionButtons">
            <button type="button" class="bwp-primary" data-bwp-action="max-all-children">${this.escapeHtml(text.botonMaximo)}</button>
            <button type="button" class="bwp-secondary" data-bwp-action="add-child">${this.escapeHtml(text.botonAgregar)}</button>
            <button type="button" class="bwp-dangerBtn" data-bwp-action="clean-fields">${this.escapeHtml(this.texts.ui.botonLimpiar)}</button>
            <button type="button" class="bwp-ghost" data-bwp-action="share-whatsapp">${this.escapeHtml(this.texts.ui.botonCompartir)}</button>
          </div>
        </div>

        <div class="bwp-targetCardsGrid">
          <div class="bwp-targetCard">
            <small>Meta Semanal Sugerida por Hijo</small>
            <strong>${this.money(suggestedWeeklyChildOrder)} / sem</strong>
            <span>Compras acumuladas</span>
          </div>
        </div>

        <div class="bwp-grid2">
          <div>
            <div class="bwp-panel bwp-soft">
              <h3>Bonos por tus Distribuidores Hijos Nuevos</h3>
              <p class="bwp-sub">Hasta ${this.fmt(this.max.linajeChild)} PB por cada Hijo Nuevo en sus primeras 6 semanas comerciales.</p>
              <div class="bwp-children">${childHtml}</div>
            </div>
            <div class="bwp-panel" style="margin-top:14px">
              <h3>Beneficios para tu siguiente catálogo</h3>
              <p class="bwp-sub">Indica cuántos Distribuidores Hijos esperas clasificar y revisa los beneficios que podrías obtener.</p>
              <div class="bwp-inlineFields" style="grid-template-columns:1fr;">
                ${this.inputField({ label: 'Hijos que proyectas clasificar', value: s.qualifiedChildren, field: 'linaje-qualified', id: `${this.uid}-linaje-qualified` })}
              </div>
              <div class="bwp-switchLine">
                <div class="bwp-switchCopy">
                  <strong>¿Nombrarás al menos 1 Distribuidor Hijo en este catálogo?</strong>
                  <span>Al nombrar al menos 1 Distribuidor Hijo, puedes obtener 6% de descuento adicional y 100% sobre los PB de tus Hijos en el siguiente catálogo.</span>
                </div>
                <div class="bwp-toggleGroup">
                  <button type="button" class="bwp-toggleBtn${s.namedNewChild ? ' is-active' : ''}" data-bwp-action="set-toggle" data-key="namedNewChild" data-value="true">Sí</button>
                  <button type="button" class="bwp-toggleBtn${!s.namedNewChild ? ' is-active' : ''}" data-bwp-action="set-toggle" data-key="namedNewChild" data-value="false">No</button>
                </div>
              </div>
              <div class="bwp-metricGrid">
                <div class="bwp-metric"><small>Descuento próximo catálogo</small><b>${Math.round(c.d * 100)}%</b><span>Adicional a tu descuento</span></div>
                <div class="bwp-metric"><small>Sobre PB de tus Hijos</small><b>${Math.round(c.pr * 100)}%</b><span>Beneficio del próximo catálogo</span></div>
                <div class="bwp-metric"><small>Escala proyectada</small><b style="font-size:18px">${this.escapeHtml(c.level)}</b><span>Según tu proyección de clasificación</span></div>
              </div>
              <div class="bwp-callout"><b>Importante:</b> estos porcentajes corresponden al siguiente catálogo. El beneficio económico y los PB finales dependerán de las compras y puntos reales generados durante ese periodo.</div>
            </div>
          </div>
          ${this.resultPanel(
            'Beneficio próximo catálogo',
            `${Math.round(c.d * 100)}%`,
            'descuento',
            'Beneficios que podrías obtener según tu Linaje.',
            [
              ['Hijos que proyectas clasificar', this.fmt(s.qualifiedChildren)],
              ['Descuento adicional', `${Math.round(c.d * 100)}%`],
              ['Porcentaje sobre PB de tus Hijos', `${Math.round(c.pr * 100)}%`],
              ['Escala proyectada', c.level]
            ],
            'Bonos por Hijos Nuevos',
            `${this.fmt(c.childBonuses)} PB`
          )}
        </div>
      `;

      return this.shell('linaje', inner);
    }

    addChild() {
      this.state.linaje.children.push({
        name: `Hijo ${this.state.linaje.children.length + 1}`,
        firstOrder: 0,
        associates: 0,
        purchases: 0
      });
      this.renderWorkspace();
    }

    removeChild(index) {
      this.state.linaje.children.splice(index, 1);
      if (!this.state.linaje.children.length) {
        this.state.linaje.children = [{ name: 'Hijo 1', firstOrder: 0, associates: 0, purchases: 0 }];
      }
      this.renderWorkspace();
    }

    maxAllChildren() {
      this.state.linaje.children = this.state.linaje.children.map((ch, i) => ({
        ...ch,
        name: `Hijo ${i + 1}`,
        firstOrder: 5450,
        associates: 12,
        purchases: 32500
      }));
      this.renderWorkspace();
    }

    // ---------- Círculo del Presidente ----------

    cpLevel(n) {
      if (n >= 45) return 'PLATINO';
      if (n >= 30) return 'ORO';
      if (n >= 20) return 'PLATA';
      if (n >= 10) return 'BRONCE';
      return null;
    }

    cpDiscount(level, cp) {
      if (!level) return 0;
      if (level === 'BRONCE') return this.state.circulo.classifiedChildren >= 15 ? 0.08 : 0.06;
      const base = { PLATA: 0.11, ORO: 0.15, PLATINO: 0.17 }[level];
      if (cp >= 8) return base + 0.03;
      if (cp >= 4) return base + 0.02;
      if (cp >= 1) return base + 0.01;
      return base;
    }

    calcCirculo() {
      const s = this.state.circulo;
      const eligible = ['LIDER', 'MASTER'].includes(s.classification) && s.classifiedChildren >= 10;
      const level = eligible ? this.cpLevel(s.classifiedChildren) : null;
      const cpEffective = Math.min(Number(s.cpChildren || 0), Number(s.classifiedChildren || 0));
      const capped = Number(s.cpChildren || 0) > cpEffective;
      const d = this.cpDiscount(level, cpEffective);
      const pr = level ? this.rules.cp.points[level] : 0;
      const bonus = level && s.firstTime ? this.rules.cp.bonus[level] : 0;
      return { eligible, level, d, pr, bonus, cpEffective, capped };
    }

    renderCirculo() {
      const c = this.calcCirculo();
      const s = this.state.circulo;
      const text = this.texts.programs.circulo;
      const missingChildren = Math.max(0, 10 - s.classifiedChildren);

      const inner = `
        <div class="bwp-topAction">
          <div class="bwp-copy">
            <strong>${this.escapeHtml(text.topTitulo)}</strong>
            <span>${this.escapeHtml(text.topDescripcion)}</span>
          </div>
          <div class="bwp-topActionButtons">
            <button type="button" class="bwp-primary" data-bwp-action="max-circulo">${this.escapeHtml(text.botonMaximo)}</button>
            <button type="button" class="bwp-dangerBtn" data-bwp-action="clean-fields">${this.escapeHtml(this.texts.ui.botonLimpiar)}</button>
            <button type="button" class="bwp-ghost" data-bwp-action="share-whatsapp">${this.escapeHtml(this.texts.ui.botonCompartir)}</button>
          </div>
        </div>

        <div class="bwp-grid2">
          <div>
            <div class="bwp-panel bwp-soft">
              <h3>Estructura del Círculo</h3>
              <p class="bwp-sub">Ingresa tu estructura. Recuerda que tus Hijos CP también forman parte de tus Hijos clasificados.</p>
              <div class="bwp-inlineFields">
                <div class="bwp-field">
                  <label>Tu clasificación personal</label>
                  <select class="bwp-select" data-bwp-select="circulo-classification">
                    <option value="LIDER"${s.classification === 'LIDER' ? ' selected' : ''}>Distribuidor Líder</option>
                    <option value="MASTER"${s.classification === 'MASTER' ? ' selected' : ''}>Distribuidor Máster</option>
                  </select>
                </div>
                ${this.inputField({ label: 'Hijos clasificados', value: s.classifiedChildren, field: 'circulo-classified', id: `${this.uid}-circulo-classified` })}
                ${this.inputField({ label: 'Hijos CP (Líder/Máster con 10+ Hijos)', value: s.cpChildren, field: 'circulo-cp', id: `${this.uid}-circulo-cp` })}
              </div>
              ${c.capped ? `<div class="bwp-callout" style="border-left-color:var(--bwp-danger)"><b>Revisa tu captura:</b> registraste más Hijos CP (${this.fmt(s.cpChildren)}) que Hijos clasificados (${this.fmt(s.classifiedChildren)}). El cálculo considera únicamente ${this.fmt(c.cpEffective)} Hijos CP.</div>` : ''}
              <div class="bwp-metricGrid">
                <div class="bwp-metric"><small>Estatus Círculo</small><b>${c.level || 'No clasifica'}</b><span>${c.eligible ? 'Requisitos cumplidos' : ['LIDER', 'MASTER'].includes(s.classification) ? `Faltan ${missingChildren} Hijos clasificados` : 'Se requiere nivel Líder o Máster'}</span></div>
                <div class="bwp-metric"><small>Descuento próximo catálogo</small><b>${Math.round(c.d * 100)}%</b><span>Sobre compras netas de tu Linaje</span></div>
                <div class="bwp-metric"><small>Sobre PB de tus Hijos</small><b>${Math.round(c.pr * 100)}%</b><span>Porcentaje del próximo catálogo</span></div>
              </div>
            </div>
            <div class="bwp-panel" style="margin-top:14px">
              <h3>Bono de primera clasificación</h3>
              <p class="bwp-sub">Este Bono Único aplica solo la primera vez que alcanzas este nivel de Círculo del Presidente.</p>
              <div class="bwp-switchLine bwp-switchLine--flush">
                <div class="bwp-switchCopy">
                  <strong>¿Es tu primera vez en este nivel de Círculo?</strong>
                  <span>El Bono Único se calculará según el nivel que alcances: Bronce, Plata, Oro o Platino.</span>
                </div>
                <div class="bwp-toggleGroup">
                  <button type="button" class="bwp-toggleBtn${s.firstTime ? ' is-active' : ''}" data-bwp-action="set-toggle" data-key="firstTime" data-value="true">Sí</button>
                  <button type="button" class="bwp-toggleBtn${!s.firstTime ? ' is-active' : ''}" data-bwp-action="set-toggle" data-key="firstTime" data-value="false">No</button>
                </div>
              </div>
              <div class="bwp-targetCard bwp-targetCard--gold">
                <small>Bono único proyectado</small>
                <strong>${this.fmt(c.bonus)} PB</strong>
                <span>${c.level ? `Por primera clasificación a Círculo ${c.level}` : 'Clasifica en un nivel de Círculo para proyectarlo'}</span>
              </div>
            </div>
            <div class="bwp-callout"><b>Importante:</b> el descuento y el porcentaje sobre PB aplican en el siguiente catálogo. El resultado final dependerá de la actividad real de ese periodo.</div>
            <div class="bwp-callout"><b>¿Vas por Círculo Diamante?</b> Al alcanzar Círculo Platino + Distribuidor Máster + 8 Hijos CP o más, puedes avanzar a Círculo Diamante. Consulta las bases vigentes para conocer las condiciones del Bono Único de 1,000,000 PB y del beneficio de automóvil.</div>
          </div>
          ${this.resultPanel(
            'Beneficios próximo catálogo',
            c.level || '—',
            '',
            'Beneficios que podrías obtener con esta estructura.',
            [
              ['Descuento sobre compras netas de Linaje', `${Math.round(c.d * 100)}%`],
              ['Porcentaje sobre PB de tus Hijos', `${Math.round(c.pr * 100)}%`],
              ['Hijos clasificados', this.fmt(s.classifiedChildren)],
              ['Hijos en Círculo del Presidente', this.fmt(c.cpEffective)]
            ],
            'Bono de primera clasificación',
            `${this.fmt(c.bonus)} PB`
          )}
        </div>
      `;

      return this.shell('circulo', inner);
    }

    maxCirculo() {
      this.state.circulo = { classification: 'MASTER', classifiedChildren: 45, cpChildren: 8, firstTime: true };
      this.renderWorkspace();
    }

    // ---------- Acciones generales ----------

    cleanFields() {
      const id = this.state.active;
      if (id === 'arranca') {
        this.state.arranca.weeks = Array.from({ length: 6 }, () => ({ order: 0, associates: 0 }));
      } else if (id === 'catalogo') {
        this.state.desarrollo.target = 'BASE';
        this.state.desarrollo.weeksCount = 4;
        this.state.desarrollo.firstLeader = false;
        this.state.desarrollo.firstMaster = false;
        this.state.desarrollo.hadLeaderBonusBefore = true;
        this.state.recluta.weeksCount = 4;
        this.state.recluta.weeks = Array.from({ length: 5 }, () => ({ recruits: 0, referrals: 0 }));
        this.state.recluta.continuity = 0;
        this.buildDevelopmentPlan();
        return;
      } else if (id === 'linaje') {
        this.state.linaje.children = [{ name: 'Hijo 1', firstOrder: 0, associates: 0, purchases: 0 }];
        this.state.linaje.qualifiedChildren = 0;
        this.state.linaje.namedNewChild = false;
      } else if (id === 'circulo') {
        this.state.circulo = { classification: 'LIDER', classifiedChildren: 10, cpChildren: 0, firstTime: true };
      }
      this.renderWorkspace();
    }

    shareSummary() {
      const id = this.state.active;
      const text = this.texts.programs[id];
      const head = [];
      const actions = [];
      const close = [];

      if (id === 'arranca') {
        const c = this.calcArranca();
        const w = this.state.arranca.weeks;
        head.push(`Mi meta en ${text.nombre}`, '', `Voy por ${this.fmt(c.pb)} Puntos Betterware en mis primeras 6 semanas.`);
        if (w[0].order) actions.push(`Primer pedido de ${this.money(w[0].order)} en mi semana 1`);
        const rest = this.avg(w.slice(1).map((x) => Number(x.order || 0)));
        if (rest) actions.push(`Pedido de ${this.money(rest)} las siguientes semanas`);
        const assoc = this.avg(w.map((x) => Number(x.associates || 0)));
        if (assoc) actions.push(`${this.plural(assoc, 'Asociado Nuevo', 'Asociados Nuevos')} por semana`);
        close.push(`Al cierre: ${this.fmt(c.associates)} Asociados Nuevos y ${this.money(c.total)} de compra acumulada.`);
      } else if (id === 'catalogo') {
        const cRec = this.calcRecluta();
        const cDev = this.calcDesarrollo();
        const sDev = this.state.desarrollo;
        const sRec = this.state.recluta;
        const totalPB = cRec.total + cDev.bonus;

        const nivel = cDev.achieved === 'NO_CLASIFICA' ? 'clasificar' : `cerrar en ${cDev.achieved.replace('_', ' ')}`;
        head.push(`Mi meta en ${text.nombre}`, '', `Voy a ${nivel} y proyectar ${this.fmt(totalPB)} PB este catálogo.`);

        const wkSales = this.avg(sDev.weeks.slice(0, sDev.weeksCount).map((v) => this.n0(v)));
        if (wkSales) actions.push(`${this.money(wkSales)} de compra de grupo cada semana`);

        const rec = this.avg(sRec.weeks.slice(0, sDev.weeksCount).map((x) => Number(x.recruits || 0)));
        const ref = this.avg(sRec.weeks.slice(0, sDev.weeksCount).map((x) => Number(x.referrals || 0)));
        if (rec) actions.push(`${this.plural(rec, 'Recluta directo', 'Reclutas directos')} por semana`);
        if (ref) actions.push(`${this.plural(ref, 'Referido o Reactivación', 'Referidos o Reactivaciones')} por semana`);
        if (sRec.continuity) actions.push(`Acompañar a ${this.plural(sRec.continuity, 'asociado', 'asociados')} hasta su segundo catálogo`);

        if (cDev.achieved !== 'NO_CLASIFICA') {
          close.push(`Beneficios del próximo catálogo: ${Math.round(cDev.discountPct)}% de descuento y ${Math.round(cDev.pointsPct)}% de puntos.`);
        }
        close.push(`Al cierre: ${this.fmt(cRec.recruits + cRec.refs)} Asociados Nuevos y ${this.money(cDev.total)} de compra grupal.`);
      } else if (id === 'linaje') {
        const c = this.calcLinaje();
        const s = this.state.linaje;
        head.push(`Mi meta en ${text.nombre}`, '', `Voy por ${this.fmt(c.childBonuses)} Puntos Betterware con mis Distribuidores Hijos Nuevos.`);
        c.children.forEach((ch) => {
          const detalle = [];
          if (ch.firstOrder) detalle.push(`primer pedido de ${this.money(ch.firstOrder)}`);
          if (ch.associates) detalle.push(`${this.fmt(ch.associates)} Asociados Nuevos`);
          if (ch.purchases) detalle.push(`${this.money(ch.purchases)} de compra`);
          if (detalle.length) actions.push(`${ch.name}: ${detalle.join(', ')}`);
        });
        if (s.namedNewChild) actions.push('Nombrar al menos 1 Distribuidor Hijo este catálogo');
        if (s.qualifiedChildren) actions.push(`Clasificar ${this.plural(s.qualifiedChildren, 'Hijo', 'Hijos')}`);
        if (c.d) {
          close.push(
            `Desbloqueo para el próximo catálogo: ${Math.round(c.d * 100)}% de descuento adicional y ${Math.round(c.pr * 100)}% sobre los PB de mis Hijos.`
          );
        }
      } else {
        const c = this.calcCirculo();
        const s = this.state.circulo;
        head.push(
          `Mi meta en ${text.nombre}`,
          '',
          c.level ? `Voy por el Círculo ${c.level} este catálogo.` : 'Voy a abrir mi Círculo del Presidente este catálogo.'
        );
        actions.push(`Clasificar como Distribuidor ${s.classification === 'MASTER' ? 'Máster' : 'Líder'}`);
        actions.push(`Llegar a ${this.fmt(Math.max(10, s.classifiedChildren))} Hijos clasificados`);
        if (c.cpEffective) actions.push(`Llevar a ${this.plural(c.cpEffective, 'Hijo', 'Hijos')} al Círculo del Presidente`);
        if (c.level) {
          close.push(`Desbloqueo para el próximo catálogo: ${Math.round(c.d * 100)}% de descuento y ${Math.round(c.pr * 100)}% sobre los PB de mis Hijos.`);
        }
        if (c.bonus) close.push(`Bono de primera clasificación: ${this.fmt(c.bonus)} PB.`);
      }

      const lines = head.slice();
      if (actions.length) {
        lines.push('', 'Para lograrlo me comprometo a:');
        actions.forEach((a) => lines.push(`· ${a}`));
      }
      if (close.length) lines.push('', ...close);

      return lines.join('\n');
    }

    shareWhatsApp() {
      window.open(`https://wa.me/?text=${encodeURIComponent(this.shareSummary())}`, '_blank');
    }

    // ---------- Estado / render raíz ----------

    setStatus(type, message) {
      if (!this.elements.status) return;
      this.elements.status.hidden = false;
      this.elements.status.className = `bwp-status is-${type}`;
      this.elements.status.textContent = message;
    }

    clearStatus() {
      if (!this.elements.status) return;
      this.elements.status.hidden = true;
      this.elements.status.textContent = '';
      this.elements.status.className = 'bwp-status';
    }

    renderWorkspace() {
      const activeEl = document.activeElement;
      const activeId = activeEl && this.root.contains(activeEl) ? activeEl.id : null;
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      let selectionStart = null;
      let selectionEnd = null;

      if (activeId && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) {
        try {
          selectionStart = activeEl.selectionStart;
          selectionEnd = activeEl.selectionEnd;
        } catch (error) {
          // Algunos tipos de input no exponen selectionStart/selectionEnd.
        }
      }

      const renderers = {
        arranca: () => this.renderArranca(),
        catalogo: () => this.renderCatalogo(),
        linaje: () => this.renderLinaje(),
        circulo: () => this.renderCirculo()
      };

      this.elements.workspace.innerHTML = renderers[this.state.active]();
      this.saveState();

      // Reconstruir el HTML puede cambiar la altura de la página (p. ej. al
      // aparecer/ocultarse paneles condicionales), lo que hace que el
      // navegador reubique el scroll. Restauramos la posición exacta para
      // que la vista no "salte" mientras el usuario está escribiendo.
      window.scrollTo(scrollX, scrollY);

      if (activeId) {
        requestAnimationFrame(() => {
          const el = document.getElementById(activeId);
          if (!el) return;
          this.skipNextAutoSelect = true;
          el.focus({ preventScroll: true });
          if (selectionStart !== null && selectionEnd !== null) {
            try {
              el.setSelectionRange(selectionStart, selectionEnd);
            } catch (error) {
              // El foco ya quedó restaurado aunque no se pueda fijar el rango.
            }
          }
          window.scrollTo(scrollX, scrollY);
        });
      }
    }

    render() {
      this.renderNav();
      this.renderWorkspace();
    }
  }

  function init(root = document) {
    root.querySelectorAll(SELECTOR).forEach((element) => {
      if (element.dataset.bwProyectaInitialized === 'true') return;
      element.dataset.bwProyectaInitialized = 'true';
      new BetterwareProyecta(element);
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
